import { useNavigate } from 'react-router-dom';
import type { ClientSegment } from '@/api/base-types';
import { useClientList, useModules } from '@/api/base';
import { Button } from '@/components/kit/Button';
import { FilterBar, FilterChip, MultiFilterChip, SearchInput } from '@/components/kit/Filters';
import { PageHeader, Segments } from '@/components/kit/Layout';
import { TextLink } from '@/components/kit/Controls';
import { Who } from '@/components/kit/Person';
import { MobileSub } from '@/components/kit/Table';
import { formatPhone } from '@/lib/phone';
import { stageTitle } from '@/features/crm/labels';
import { rolesLine } from '../shared/labels';
import { ListPanel } from '../shared/ListPanel';
import { useDebouncedSearch } from '../shared/useDebouncedSearch';
import { joinList, listOf, useListParams } from '../shared/useListParams';

const SEGMENTS: { value: ClientSegment; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'head', label: 'Руководители команд' },
  { value: 'contact', label: 'Контакты студий' },
  { value: 'participant', label: 'Участники' },
  { value: 'rep', label: 'Представители' },
  { value: 'archive', label: 'Архив' },
];

const SIGNAL_TEXT = { possible_duplicate: 'возможный дубль', no_responsible: 'ответственный не назначен' };

/**
 * Список клиентов (БЛ 11.7, viewList в макете): Клиент · Телефон ·
 * Город · Команда · Роли. Колонка «Роли», а не «Статус»: статус в ките —
 * про жизненный цикл записи.
 */
export function ClientsListPage() {
  const navigate = useNavigate();
  const { values, page, set } = useListParams(['segment', 'cityId', 'responsible', 'attention', 'deals', 'q'] as const);
  // Колонка и фильтр «Сделки» — за флагом deals (доработка M2-1 п. 9)
  const withDeals = Boolean(useModules().data?.deals);
  const segment = (values.segment as ClientSegment) ?? 'all';
  const [text, setText] = useDebouncedSearch(values.q, (q) => set({ q }));
  const list = useClientList({ ...values, deals: withDeals ? values.deals : null, segment, page });
  const data = list.data;
  const filtered = Boolean(values.cityId || values.responsible || values.attention || values.deals || values.q);

  const head = (
    <tr>
      <th className="c-name srt">
        Клиент <span className="ar">▲</span>
      </th>
      <th className="c-ph">Телефон</th>
      <th className="c-city">Город</th>
      <th className="c-team">Команда</th>
      <th className="c-role">Роли</th>
      {withDeals ? <th className="c-deal">Сделки</th> : null}
    </tr>
  );

  return (
    <div className="main">
      <PageHeader title="Клиенты" count={data?.segments.all}>
        <Button tone="primary" onClick={() => navigate('/clients/new')}>
          ＋ Добавить клиента
        </Button>
      </PageHeader>

      <Segments
        value={segment}
        onChange={(s) => set({ segment: s === 'all' ? null : s, cityId: null, attention: null })}
        items={SEGMENTS.map((s) => ({ ...s, count: data?.segments[s.value] }))}
      />

      <FilterBar search={<SearchInput value={text} onChange={setText} placeholder="ФИО, телефон, почта, команда" />}>
        <MultiFilterChip
          label="Город"
          values={listOf(values.cityId)}
          onChange={(v) => set({ cityId: joinList(v) })}
          options={(data?.facets.cities ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        <MultiFilterChip
          label="Ответственный"
          values={listOf(values.responsible)}
          onChange={(v) => set({ responsible: joinList(v) })}
          options={(data?.facets.responsibles ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
        />
        {withDeals ? (
          <FilterChip
            label="Сделки"
            value={values.deals}
            onChange={(deals) => set({ deals })}
            options={(data?.facets.deals ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
          />
        ) : null}
        {segment !== 'archive' ? (
          <FilterChip
            label="Требует внимания"
            value={values.attention}
            onChange={(attention) => set({ attention })}
            options={(data?.facets.attention ?? []).map((f) => ({ value: f.value, label: f.label, side: f.count }))}
          />
        ) : null}
      </FilterBar>

      <ListPanel
        head={head}
        cols={withDeals ? 6 : 5}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        errorTitle="Не удалось загрузить клиентов."
        empty={
          data && !data.items.length ? (
            values.q ? (
              <>
                По запросу «{values.q}» никого не нашлось.
                <br />
                Нет в базе? <TextLink onClick={() => navigate('/clients/new')}>Создать клиента</TextLink>
              </>
            ) : filtered ? (
              <>
                По этим условиям никого не нашлось.
                <br />
                <TextLink onClick={() => set({ cityId: null, responsible: null, attention: null, deals: null, q: null })}>
                  Сбросить фильтры
                </TextLink>
              </>
            ) : (
              <>
                Клиентов пока нет.
                <br />
                <TextLink onClick={() => navigate('/clients/new')}>Создать клиента</TextLink>
              </>
            )
          ) : null
        }
        paging={
          data
            ? { page, pageSize: data.pageSize, total: data.total, shown: data.items.length, onPage: (p) => set({ page: String(p) }) }
            : undefined
        }
        rows={data?.items.map((c) => (
          <tr key={c.id} className="clk" onClick={() => navigate(`/clients/${c.id}`)}>
            <td data-col="primary">
              <Who
                initials={c.initials}
                name={c.fullName}
                sub={
                  <>
                    {c.signal ? <div className="sub wtx">{SIGNAL_TEXT[c.signal]}</div> : null}
                    {/* Роли на телефоне — строкой под именем: значение справа
                        не переносится, и длинный список ролей вытеснял ФИО */}
                    <MobileSub>{c.roles.length ? rolesLine(c.roles, c.studioRoles) : 'Без связей'}</MobileSub>
                  </>
                }
              />
            </td>
            <td className="num muted" data-col="hide">
              {c.phone ? formatPhone(c.phone) : <span className="na">—</span>}
            </td>
            <td data-col="hide">{c.city?.name ?? <span className="na">—</span>}</td>
            {/* Каждая команда своей строкой, под названием «рук. …», если руководит
                не сам клиент: команды часто называются одинаково (доработка M2-1 п. 4) */}
            <td data-col="hide">
              {c.teams.length ? (
                <>
                  {c.teams.map((t) => (
                    <div key={t.id} className={c.teamsViaWard ? 'tl muted' : 'tl'}>
                      {t.name}
                      {t.head.id !== c.id ? <div className="sub">рук. {t.head.shortName}</div> : null}
                    </div>
                  ))}
                  {c.teamsViaWard ? <div className="sub">через подопечного</div> : null}
                </>
              ) : (
                <span className="na">—</span>
              )}
            </td>
            <td data-col="hide">
              {c.roles.length ? rolesLine(c.roles, c.studioRoles) : <span className="dim">Без связей</span>}
            </td>
            {withDeals ? (
              <td data-col="hide">
                {c.deals.current ? (
                  <>
                    <div>
                      {c.deals.current.eventCity} · {stageTitle(c.deals.current.stage)}
                    </div>
                    {c.deals.openCount || c.deals.doneCount ? (
                      <div className="sub">
                        {[
                          c.deals.openCount ? `ещё в работе: ${c.deals.openCount}` : null,
                          c.deals.doneCount ? `завершённых: ${c.deals.doneCount}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    ) : null}
                  </>
                ) : c.deals.doneCount ? (
                  <span className="muted">завершённых: {c.deals.doneCount}</span>
                ) : (
                  <span className="na">—</span>
                )}
              </td>
            ) : null}
          </tr>
        ))}
      />
    </div>
  );
}
